/**
 * TriageMix — the triage breakdown of a hospital's current queue.
 *
 * A proportional stacked bar (segment width ∝ people waiting at that level)
 * plus a textual legend. Colour is the triage system's own language, but it is
 * never the only channel: the legend states each level's name and count, so the
 * breakdown is fully legible to colourblind users and screen readers
 * (WCAG 2.1 AA — see DECISIONS.md "never rely on colour alone").
 *
 */

import type { TriageReading } from "@/api/generated/model";
import { triageColor } from "@/lib/triage";

interface TriageMixProps {
  /** Per-triage readings present in the latest batch. */
  triage: TriageReading[];
  /** Total people waiting (sum across levels). */
  total: number;
}

export function TriageMix({ triage, total }: TriageMixProps) {
  const present = [...triage]
    .filter((t) => t.queueLength > 0)
    .sort((a, b) => a.severityRank - b.severityRank);

  if (total <= 0 || present.length === 0) {
    return <p className="triage-mix__empty">Nessuno in attesa al momento.</p>;
  }

  const summary = present.map((t) => `${t.queueLength} ${t.labelIt}`).join(", ");

  return (
    <div className="triage-mix">
      <div className="triage-mix__bar" role="img" aria-label={`Distribuzione triage: ${summary}`}>
        {present.map((t) => (
          <span
            key={t.code}
            className="triage-mix__segment"
            style={{ flexGrow: t.queueLength, backgroundColor: triageColor(t.code) }}
          />
        ))}
      </div>
      <ul className="triage-mix__legend">
        {present.map((t) => (
          <li key={t.code} className="triage-mix__legend-item">
            <span
              className="triage-mix__swatch"
              style={{ backgroundColor: triageColor(t.code) }}
              aria-hidden="true"
            />
            <span className="triage-mix__legend-label">{t.labelIt}</span>
            <span className="triage-mix__legend-count">{t.queueLength}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
